import React, { FC } from "react";
import { useQuery } from "@apollo/client";
import PageLoader from "Components/loader/pageLoader";
import instStopsGQL from "./gqlQuery";

interface Props {
  children: React.ReactElement; // expects a single React element
  instId: string | number;
  locId?: string | number;
  tourId?: string | number;
}

type Location = {
  locId: string | number;
  tours: { tourid: string | number; status: string }[];
};

const WithLocId: FC<Props> = ({ children, instId, locId, tourId }) => {
  // Helper to check if instId contains alphabetic characters
  const isAlpha = (val: string | number): boolean => {
    return typeof val === "string" ? /[a-z]/i.test(val) : false;
  };

  // Determine whether to skip the query.
  // If locId exists and instId is valid (i.e. does not contain alphabetic characters), we can skip the query.
  const shouldSkip = !!locId && !isAlpha(instId) && !!tourId;

  // Use the query with the skip option
  const { data, loading } = useQuery(instStopsGQL, {
    variables: { inst_id: instId },
    fetchPolicy: "cache-and-network",
    skip: shouldSkip,
  });

  // If the query is not skipped and is still loading, show the loader
  if (loading && !shouldSkip) {
    return <PageLoader />;
  }

  // Determine the final instId:
  // If we need to query (because locId is missing or instId is in the wrong format) and data is available, use data.
  // Otherwise, use the provided instId.
  const finalInstId =
    (!locId || isAlpha(instId)) && data && data.institutions
      ? data.institutions.inst_id
      : instId;

  // Determine the final locId:
  // If we need to query and data is available, find the live location or use the first location.
  // Otherwise, use the provided locId.
  const finalLocId = locId ?? data.institutions?.locations[0].locId;

  const selectedLocation = data?.institutions?.locations.find(
    (loc: Location) => loc.locId === parseInt(finalLocId as string, 10)
  );

  const liveTour = selectedLocation?.tours.find(
    (tour: { tourid: string | number; status: string }) =>
      tour.status === "live"
  );
  // Clone and enhance the children with the final instId and locId values
  const enhancedChildren = React.cloneElement(children, {
    ...children.props,
    instId: `${finalInstId}`,
    locId: `${finalLocId}`,
    tourId: liveTour?.tourid || selectedLocation?.tours[0]?.tourid,
  });

  return <div>{enhancedChildren}</div>;
};

export default WithLocId;
try {
    // @ts-ignore
    WithLocId.displayName = "WithLocId";
    // @ts-ignore
    WithLocId.__docgenInfo = { "description": "", "displayName": "WithLocId", "props": { "instId": { "defaultValue": null, "description": "", "name": "instId", "required": true, "type": { "name": "ReactText" } }, "locId": { "defaultValue": null, "description": "", "name": "locId", "required": false, "type": { "name": "ReactText" } }, "tourId": { "defaultValue": null, "description": "", "name": "tourId", "required": false, "type": { "name": "ReactText" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/hoc/WithLocId/WithLocId.tsx#WithLocId"] = { docgenInfo: WithLocId.__docgenInfo, name: "WithLocId", path: "src/shared/hoc/WithLocId/WithLocId.tsx#WithLocId" };
}
catch (__react_docgen_typescript_loader_error) { }